import { ShieldCheck, Building2, Banknote, Database, Target, Gauge, Zap } from "lucide-react";
import type { IconCardGridComponent } from "../../types";
import { JSX } from "react";

// Classes LITTÉRALES par breakpoint : un `md:${...}` construit par gabarit n'est
// pas détectable par le scanner Tailwind et peut disparaître du CSS généré.
const MD_COLS: Record<number, string> = {
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
};
const LG_COLS: Record<number, string> = {
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
};

function toPerRow(v: unknown): 2 | 3 | 4 {
  const n = Number(v);
  return n >= 2 && n <= 4 ? (n as 2 | 3 | 4) : 2;
}

function Card({
  icon,
  title,
  text,
  iconMap,
}: {
  icon: string;
  title: string;
  text: string;
  iconMap: Record<string, JSX.Element>;
}) {
  return (
    <div className="h-full rounded-lg bg-[#005CA9] p-5 text-white ring-1 ring-black/10 md:p-7 lg:px-4 lg:py-7">
      {/* Mobile : icône à gauche, texte à droite (rangée compacte) ; md+ : colonne centrée. */}
      <div className="flex h-full flex-row items-center gap-4 text-left md:flex-col md:gap-0 md:text-center">
        {/* Grande pastille : l'icône est le repère visuel de la carte quand il n'y a
            pas de photo. À 48 px elle se lisait comme une puce de liste. */}
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-white/10 md:h-16 md:w-16">
          {iconMap[icon] ?? <ShieldCheck className="h-7 w-7" />}
        </span>
        <div className="min-w-0 md:mt-3">
          <h3 className="text-base xl:text-lg md:text-[17px] font-semibold">{title}</h3>
          {text ? <p className="mt-1 md:mt-2 text-sm xl:text-base leading-6 opacity-90">{text}</p> : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Grille de cartes à icône.
 *
 * UNE SEULE grille, à toutes les largeurs. Le découpage précédent taillait la
 * dernière rangée d'après le nombre de colonnes du BUREAU, puis la rendait dans
 * une grille séparée : sur tablette, où la grille passe à deux colonnes, cinq
 * cartes tombaient en 2, puis 1, puis 2 centrées. Le rythme des colonnes était
 * cassé à toutes les largeurs intermédiaires.
 *
 * Le champ `centerLastRow` reste accepté, des contenus le portent, mais il ne
 * découpe plus rien : une rangée incomplète se cale à gauche, dans le rythme des
 * rangées pleines.
 */
export function IconCardGridRenderer({ component }: { component: IconCardGridComponent }) {
  const { items, cardsPerRowTablet, cardsPerRowDesktop } = component.content;
  const tablet = toPerRow(cardsPerRowTablet);
  const desktop = toPerRow(cardsPerRowDesktop);
  // Mobile (< md) : UNE colonne pleine largeur — les cartes s'empilent verticalement
  // (icône à gauche) au lieu de se serrer à deux par rangée.
  const gridCols = `grid-cols-1 ${MD_COLS[tablet]} ${LG_COLS[desktop]}`;
  const iconMap: Record<string, JSX.Element> = {
    ShieldCheck: <ShieldCheck className="h-7 w-7" />,
    Building2: <Building2 className="h-7 w-7" />,
    Banknote: <Banknote className="h-7 w-7" />,
    Database: <Database className="h-7 w-7" />,
    Target: <Target className="h-7 w-7" />,
    Gauge: <Gauge className="h-7 w-7" />,
    Zap: <Zap className="h-7 w-7" />,
  };

  return (
    <section className="w-full">
      {/* justify-items par défaut (stretch) : chaque carte REMPLIT sa colonne, et
          items-stretch égalise les hauteurs d'une rangée. */}
      <div className={`grid w-full gap-4 ${gridCols}`}>
        {items.map(({ icon, title, text }, i) => (
          <Card key={`${title}-${i}`} icon={icon} title={title} text={text} iconMap={iconMap} />
        ))}
      </div>
    </section>
  );
}
